import React, { useState, useMemo, useCallback } from 'react';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { addToast } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import CoursesTab from '../../components/admin/courses/CoursesTab';
import CourseFormModal from '../../components/admin/courses/CourseFormModal';
import AssignUsersModal from '../../components/admin/courses/AssignUsersModal';
import AssignDepartmentsModal from '../../components/admin/courses/AssignDepartmentsModal';
import {
  useInfiniteCourses,
  CoursesFilters,
  useDeleteCourse,
  usePublishCourse,
  useUnpublishCourse,
} from '@/hooks/queries/useCourses';
import { useDebounce } from '@/hooks/useDebounce';
import { Course } from '@/services/apis/courses';

const CoursesContainer: React.FC = () => {
  const { t } = useTranslation('admin');

  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 500);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | undefined>();
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [assignUsersModalOpen, setAssignUsersModalOpen] = useState(false);
  const [assignDepartmentsModalOpen, setAssignDepartmentsModalOpen] = useState(false);
  const [selectedCourseId, setSelectedCourseId] = useState<string | null>(null);

  const filters: CoursesFilters = useMemo(
    () => ({
      limit: 10,
      categoryId: selectedCategoryId === 'all' ? undefined : selectedCategoryId,
      search: debouncedSearch || undefined,
      useDraftData: true,
    }),
    [selectedCategoryId, debouncedSearch],
  );

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isRefetching, error } =
    useInfiniteCourses(filters);

  const courses = useMemo(() => {
    return (
      data?.pages.flatMap((page) => {
        return page.data || [];
      }) || []
    );
  }, [data]);

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const loaderRef = useInfiniteScroll({
    hasMore: !!hasNextPage && !isFetchingNextPage,
    onLoadMore: handleLoadMore,
  });

  const deleteCourseMutation = useDeleteCourse();
  const publishCourseMutation = usePublishCourse();
  const unpublishCourseMutation = useUnpublishCourse();

  const handleClearFilters = useCallback(() => {
    setSearchQuery('');
    setSelectedCategoryId(undefined);
  }, []);

  const handleEdit = useCallback((course: Course) => {
    setSelectedCourseId(course.id);
    setFormModalOpen(true);
  }, []);

  const handleDelete = useCallback(
    async (course: Course) => {
      try {
        await deleteCourseMutation.mutateAsync(course.id);
        addToast({
          title: t('courses.actions.deleteSuccess'),
          color: 'success',
        });
      } catch (error) {
        addToast({
          title: t('courses.actions.deleteError'),
          color: 'danger',
        });
      }
    },
    [deleteCourseMutation, t],
  );

  const handlePublish = useCallback(
    async (course: Course) => {
      try {
        await publishCourseMutation.mutateAsync(course.id);
        addToast({
          title: t('courses.actions.publishSuccess'),
          color: 'success',
        });
      } catch (error) {
        addToast({
          title: t('courses.actions.publishError'),
          color: 'danger',
        });
      }
    },
    [publishCourseMutation, t],
  );

  const handleUnpublish = useCallback(
    async (course: Course) => {
      try {
        await unpublishCourseMutation.mutateAsync(course.id);
        addToast({
          title: t('courses.actions.unpublishSuccess'),
          color: 'success',
        });
      } catch (error) {
        addToast({
          title: t('courses.actions.unpublishError'),
          color: 'danger',
        });
      }
    },
    [unpublishCourseMutation, t],
  );

  const handleAssignUsers = useCallback((course: Course) => {
    setSelectedCourseId(course.id);
    setAssignUsersModalOpen(true);
  }, []);

  const handleAssignDepartments = useCallback((course: Course) => {
    setSelectedCourseId(course.id);
    setAssignDepartmentsModalOpen(true);
  }, []);

  const handleCreate = useCallback(() => {
    setSelectedCourseId(null);
    setFormModalOpen(true);
  }, []);

  const hasActiveFilters = !!(selectedCategoryId || searchQuery);

  return (
    <>
      <CoursesTab
        courses={courses}
        isLoading={isLoading || isRefetching}
        isFetchingNextPage={isFetchingNextPage}
        error={error?.message || null}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        selectedCategoryId={selectedCategoryId}
        onCategoryChange={setSelectedCategoryId}
        hasActiveFilters={hasActiveFilters}
        onClearFilters={handleClearFilters}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onPublish={handlePublish}
        onUnpublish={handleUnpublish}
        onAssignUsers={handleAssignUsers}
        onAssignDepartments={handleAssignDepartments}
        isDeleting={deleteCourseMutation.isPending}
        isPublishing={publishCourseMutation.isPending}
        isUnpublishing={unpublishCourseMutation.isPending}
        loadMoreRef={loaderRef}
        hasNextPage={!!hasNextPage}
        onCreate={handleCreate}
      />

      {formModalOpen && (
        <CourseFormModal
          isOpen={formModalOpen}
          onClose={() => {
            setFormModalOpen(false);
            setSelectedCourseId(null);
          }}
          courseId={selectedCourseId}
        />
      )}

      <AssignUsersModal
        isOpen={assignUsersModalOpen}
        onClose={() => {
          setAssignUsersModalOpen(false);
          setSelectedCourseId(null);
        }}
        courseId={selectedCourseId}
      />

      <AssignDepartmentsModal
        isOpen={assignDepartmentsModalOpen}
        onClose={() => {
          setAssignDepartmentsModalOpen(false);
          setSelectedCourseId(null);
        }}
        courseId={selectedCourseId}
      />
    </>
  );
};

export default CoursesContainer;
